import { useNavigate } from "@tanstack/react-router";
import {
  Drawer,
  Search,
  SortableCustomTable,
} from "@totalenergiescode/mobility-business-rev-design-system";
import React from "react";
import { useTranslation } from "react-i18next";

import { ACCOUNT_USERS_PER_PAGE } from "@/api/graphql/accounts/getAccountUsers/constants.ts";
import { useUserCompanies } from "@/api/graphql/accounts/getUserCompanies/hooks.ts";
import {
  StyledCurrentCountOnTotalContainer,
  StyledCurrentCountOnTotalText,
} from "@/components/Agents/List/styles";
import { useFeatureFlags } from "@/contexts/featureFlags/context";
import { useFormattedDate } from "@/hooks/i18n/useFormatDate/hook";

import { AccessActionDrawer } from "./AccessActionDrawer/component";
import { fromUserCompaniesToColumns, fromUserCompaniesToRows } from "./mappers";
import {
  StyledAccessActionSelect,
  StyledAccessActionSelectWrapper,
  StyledAccessListControls,
  StyledContainer,
  StyledDivider,
  StyledRoundedLeftContainer,
  StyledSearchField,
  StyledSearchFieldContainer,
  StyledSearchFieldWrapper,
  StyledTitle,
} from "./styles";
import type { AccessAction } from "./types";
import { useBulkSelection } from "./useBulkSelection";

interface AccountsListProps {
  userId: string;
}

export function AccountsList({ userId }: Readonly<AccountsListProps>) {
  const { t: tUsers } = useTranslation("users");
  const { t: tProfiles } = useTranslation("profiles");
  const { t: tCompanies } = useTranslation("companies");
  const { t: tCommon } = useTranslation("common");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [accessAction, setAccessAction] = React.useState<AccessAction | "">("");
  const formattedDate = useFormattedDate();
  const navigate = useNavigate();
  const {
    data: listOfCompanies,
    fetchNextPage,
    hasNextPage,
    totalUserCompanies,
  } = useUserCompanies(userId, searchTerm, { paginateResponse: true });
  const { isManageUserAccessEnabled } = useFeatureFlags();
  const { selectedIds, toggleRow, toggleAll } = useBulkSelection();

  const allRowIds = listOfCompanies?.map((company) => company.id) ?? [];
  const allRowSelection: boolean | "indeterminate" = (() => {
    if (allRowIds.length > 0 && selectedIds.size === allRowIds.length) {
      return true;
    }
    if (selectedIds.size > 0) {
      return "indeterminate";
    }

    return false;
  })();

  const closeAccessActionDrawer = () => setAccessAction("");

  return (
    <StyledContainer>
      <StyledRoundedLeftContainer>
        <StyledTitle variant='title2'>
          {tUsers("users.list.subtitle.list")}
        </StyledTitle>
        <StyledAccessListControls>
          <StyledDivider />
          <StyledSearchFieldContainer>
            <StyledSearchFieldWrapper>
              <StyledSearchField
                dataTestId='text-field'
                onChange={(e) => setSearchTerm(e.target.value)}
                value={searchTerm}
                placeholder={tUsers("users.list.searchPlaceholder")}
                startIcon={<Search width={18} height={18} />}
              />
            </StyledSearchFieldWrapper>
            {isManageUserAccessEnabled && (
              <StyledAccessActionSelectWrapper>
                <StyledAccessActionSelect
                  type='options'
                  dataTestId='users__access-management-select'
                  label={tUsers("users.list.accessManagement.label")}
                  value={accessAction}
                  options={[
                    {
                      label: tUsers("users.list.accessManagement.enable"),
                      value: "enable",
                    },
                    {
                      label: tUsers("users.list.accessManagement.disable"),
                      value: "disable",
                    },
                  ]}
                  onChange={(event) => {
                    const value = event.target.value;

                    if (value === "enable" || value === "disable") {
                      setAccessAction(value);
                    }
                  }}
                />
              </StyledAccessActionSelectWrapper>
            )}
          </StyledSearchFieldContainer>
        </StyledAccessListControls>

        <SortableCustomTable
          columns={fromUserCompaniesToColumns({ tUsers })}
          rows={fromUserCompaniesToRows({
            userCompanies: listOfCompanies ?? [],
            selectedIds,
            onNavigate: (accountId) => navigate({ to: `/account/${accountId}` }),
            formattedDate,
            tCompanies,
            tProfiles,
          })}
          loadMoreButton={
            hasNextPage
              ? {
                  label: tCommon("common.pagination.nextPage", {
                    pageSize: ACCOUNT_USERS_PER_PAGE,
                  }),
                  onClick: () => fetchNextPage(),
                  dataTestId: "accounts-list_load-more-button",
                }
              : undefined
          }
          canSelectRows={true}
          allRowSelection={allRowSelection}
          onSelectAllRows={() => toggleAll(allRowIds)}
          onRowSelection={(_event, rowId) => toggleRow(rowId)}
        />

        <StyledCurrentCountOnTotalContainer>
          <StyledCurrentCountOnTotalText variant='sub3'>
            {tUsers("users.list.namesWithCurrentCountOnTotal.name", {
              count: listOfCompanies?.length ?? 0,
              total: totalUserCompanies,
            })}
          </StyledCurrentCountOnTotalText>
        </StyledCurrentCountOnTotalContainer>
      </StyledRoundedLeftContainer>
      <Drawer
        isOpen={accessAction !== ""}
        onClose={closeAccessActionDrawer}
        width='632px'
        dataTestId='accounts__access-action-drawer'
      >
        {accessAction && (
          <AccessActionDrawer
            action={accessAction}
            accountCount={selectedIds.size}
            onClose={closeAccessActionDrawer}
          />
        )}
      </Drawer>
    </StyledContainer>
  );
}
